/**
 * Letters and labels (NEW-WF-12, module id `letters`, routes under
 * `/v1/letters`). Library: imaging-docs X-01, X-02.
 *
 * Every printed letter or label for one patient is saved as a PDF in that
 * patient's Documents (kind `correspondence`, received via `system`), so the
 * paper trail is the same as the PMS's. Sheets for many patients are
 * returned without being filed to any one patient.
 */
import { z } from 'zod';

const Id = z.uuid();

export const LETTERS_PERMISSIONS = {
  /** See templates and preview letters. */
  view: 'documents.view',
  /** Print letters and labels (files them to Documents). */
  print: 'documents.upload',
  /** Edit the practice's templates. */
  manage: 'settings.manage',
} as const;

export const LetterTemplateSchema = z.object({
  key: z.string(),
  name: z.string(),
  subject: z.string().nullable(),
  body: z.string(),
  /** Patient letters, the referral letter, or clinical handouts (post-op instructions). */
  use: z.enum(['patient', 'referral', 'clinical']),
  builtin: z.boolean(),
  /** True when a practice row overrides the built-in text. */
  customized: z.boolean(),
  /**
   * Clinical handouts: CDT codes or prefixes ("D72*") on today's visit that
   * suggest it. Empty for letters.
   */
  codes: z.array(z.string()).default([]),
  /**
   * Clinical handouts still on Velora's generic text: the practice's dentist
   * has not reviewed and saved them yet (shown as a reminder, still printable).
   */
  needsReview: z.boolean().default(false),
});
export type LetterTemplate = z.infer<typeof LetterTemplateSchema>;
export const LetterTemplateListSchema = z.object({
  templates: z.array(LetterTemplateSchema),
  fields: z.array(z.string()),
  canManage: z.boolean(),
});
export type LetterTemplateList = z.infer<typeof LetterTemplateListSchema>;

export const SaveTemplateBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  /** New templates only: a patient letter (default) or a clinical handout. */
  use: z.enum(['patient', 'clinical']).optional(),
  subject: z.string().trim().max(200).nullable().optional(),
  body: z.string().min(1).max(20000),
});

export const PreviewBodySchema = z.object({
  patientId: Id,
  templateKey: z.string().regex(/^[a-z0-9_]{2,40}$/),
});
export const PreviewSchema = z.object({
  title: z.string(),
  text: z.string(),
  emptyFields: z.array(z.string()),
  unknownFields: z.array(z.string()),
});
export type LetterPreview = z.infer<typeof PreviewSchema>;

export const PrintLetterBodySchema = z.object({
  patientId: Id,
  templateKey: z.string().regex(/^[a-z0-9_]{2,40}$/),
  /** The (possibly edited) text to print; default the merged template. */
  text: z.string().min(1).max(20000).optional(),
  title: z.string().trim().min(1).max(200).optional(),
});
export const PrintedSchema = z.object({
  documentId: Id.nullable(),
  title: z.string(),
  pages: z.number().int(),
  /** Only for sheets that are not filed to one patient. */
  pdfBase64: z.string().nullable(),
});
export type Printed = z.infer<typeof PrintedSchema>;

export const LabelKindSchema = z.enum(['patient', 'carrier', 'referral']);
export const LabelFormatSchema = z.enum(['avery5160', 'dymo30252']);
export const PrintLabelsBodySchema = z.object({
  kind: LabelKindSchema,
  format: LabelFormatSchema,
  patientIds: z.array(Id).min(1).max(300),
  /** Referral labels: the specialist. */
  specialistId: Id.optional(),
  /** Avery: labels already used on the first sheet. */
  skip: z.number().int().min(0).max(29).optional(),
  copies: z.number().int().min(1).max(30).optional(),
});
export type PrintLabelsBody = z.infer<typeof PrintLabelsBodySchema>;

/* ------------------------------------------------------------------------ */
/* Batch letters: one template merged for a filtered patient list            */
/* ------------------------------------------------------------------------ */

/**
 * Patient lists a batch can be built from (one office at a time):
 * - recall_due: an active recall (continuing care) due in the date range,
 *   by default without a future scheduled visit;
 * - unscheduled_treatment: treatment-planned procedures not on a scheduled
 *   or completed visit, and no future scheduled visit.
 */
export const BATCH_FILTERS = ['recall_due', 'unscheduled_treatment'] as const;
export const BatchFilterSchema = z.enum(BATCH_FILTERS);
export type BatchFilter = z.infer<typeof BatchFilterSchema>;

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');

export const BatchListQuerySchema = z.object({
  filter: BatchFilterSchema,
  /** recall_due: due on or after (default: first of this month). */
  from: IsoDate.optional(),
  /** recall_due: due on or before (default: end of next month). */
  to: IsoDate.optional(),
  /** recall_due: also list patients who already have a future visit. */
  includeScheduled: z.enum(['true', 'false']).optional(),
});
export type BatchListQuery = z.infer<typeof BatchListQuerySchema>;

export const BatchPatientSchema = z.object({
  patientId: Id,
  /** "Last, First". */
  name: z.string(),
  /** Why the patient is on the list ("Prophy due 10/20/2026", "3 planned procedures"). */
  detail: z.string(),
  /** False when there is no street address to mail the letter to. */
  hasAddress: z.boolean(),
});
export type BatchPatient = z.infer<typeof BatchPatientSchema>;

export const BatchListSchema = z.object({
  filter: BatchFilterSchema,
  from: IsoDate.nullable(),
  to: IsoDate.nullable(),
  patients: z.array(BatchPatientSchema),
  /** True when the list was cut at the batch limit. */
  capped: z.boolean(),
});
export type BatchList = z.infer<typeof BatchListSchema>;

export const BATCH_MAX_PATIENTS = 500;

/**
 * Prints one letter per patient as a single PDF (each letter on its own
 * page). Print or save as PDF only: nothing is sent, mailed or filed.
 */
export const PrintBatchBodySchema = z.object({
  templateKey: z.string().regex(/^[a-z0-9_]{2,40}$/),
  patientIds: z.array(Id).min(1).max(BATCH_MAX_PATIENTS),
  /** Context for the audit trail (which list the batch came from). */
  filter: BatchFilterSchema.optional(),
});
export type PrintBatchBody = z.infer<typeof PrintBatchBodySchema>;

export const PrintedBatchSchema = z.object({
  title: z.string(),
  pages: z.number().int(),
  letters: z.number().int(),
  /** Merge fields left empty, with how many letters each was empty in. */
  emptyFields: z.array(z.object({ field: z.string(), letters: z.number().int() })),
  pdfBase64: z.string(),
});
export type PrintedBatch = z.infer<typeof PrintedBatchSchema>;
